import React, { useEffect, useState } from 'react';
import { getCurrentUser } from '@/lib/api';
import { NavLink } from 'react-router-dom';
import { Github, Twitter, Instagram } from 'lucide-react';
import Logo from '@/components/Logo';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [dbMeta, setDbMeta] = useState({ debug: true, database: null });

  useEffect(() => {
    // Fetch meta DB (ne bloque pas le rendu)
    fetch('/api/meta/db')
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => setDbMeta(data))
      .catch(() => {});
  }, []);

  const [user, setUser] = useState(() => getCurrentUser());

  useEffect(() => {
    const onAuthChange = () => {
      try { setUser(getCurrentUser()); } catch (e) {}
    };

    // Listen for explicit auth changes dispatched in-app
    window.addEventListener('authChange', onAuthChange);

    // Also listen for storage events to handle cross-tab login/logout
    const onStorage = (e) => {
      if (!e) return;
      if (e.key === 'tdv_current_user' || e.key === 'tdv_auth_token') {
        try { setUser(getCurrentUser()); } catch (err) {}
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('authChange', onAuthChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const handleNavClick = (e) => {
    // Force scroll to top immediately on footer link click
    try { window.scrollTo({ top: 0, behavior: 'instant' }); } catch { window.scrollTo(0,0); }
  };

  const socialLinks = [
    { icon: <Twitter className="h-5 w-5" />, href: '#' },
    { icon: <Instagram className="h-5 w-5" />, href: '#' },
    { icon: <Github className="h-5 w-5" />, href: '#' },
  ];

  return (
    <footer className="bg-secondary text-secondary-foreground border-t border-border">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
          {/* Logo and Copyright */}
          <div className="md:col-span-1 flex flex-col">
            <NavLink to="/" className="mb-4">
              <Logo className="h-12" />
            </NavLink>
            <p className="text-sm text-muted-foreground">
              &copy; {currentYear} The Design Vault. <br /> All rights reserved.
            </p>
            {dbMeta.database && (
              <div className="mt-2 inline-flex items-center rounded bg-muted py-1 text-[11px] font-mono text-muted-foreground">
                {dbMeta.database}{dbMeta.apiVersion ? ` v${dbMeta.apiVersion}` : ''}
              </div>
            )}
          </div>

          {user ? (
            <>
              {/* Navigation */}
              <div>
                <h3 className="font-semibold text-foreground mb-4">Navigate</h3>
                <ul className="space-y-2">
                  <li><NavLink to="/" onClick={handleNavClick} className="text-muted-foreground hover:text-primary transition-colors">Home</NavLink></li>
                  <li>
                    <NavLink to="/catalogue" onClick={handleNavClick} className="text-muted-foreground hover:text-primary transition-colors">Inspiration</NavLink>
                  </li>
                  <li><NavLink to="/login" onClick={handleNavClick} className="text-muted-foreground hover:text-primary transition-colors">Login</NavLink></li>
                  <li><NavLink to="/request-access" onClick={handleNavClick} className="text-muted-foreground hover:text-primary transition-colors">Request Pro Access</NavLink></li>
                </ul>
              </div>

              {/* Customer Service */}
              <div>
                <h3 className="font-semibold text-foreground mb-4">Customer Service</h3>
                <ul className="space-y-2">
                  <li><NavLink to="/white-glove-delivery" onClick={handleNavClick} className="text-muted-foreground hover:text-primary transition-colors">White-Glove Delivery Pricing And Policy</NavLink></li>
                  <li><NavLink to="/terms-conditions" onClick={handleNavClick} className="text-muted-foreground hover:text-primary transition-colors">Terms &amp; Conditions</NavLink></li>
                  <li><NavLink to="/return-policy" onClick={handleNavClick} className="text-muted-foreground hover:text-primary transition-colors">Return Policy</NavLink></li>
                </ul>
              </div>

              {/* Resources */}
              <div>
                <h3 className="font-semibold text-foreground mb-4">Resources</h3>
                <ul className="space-y-2">
                  <li><NavLink to="/measuring-for-delivery" onClick={handleNavClick} className="text-muted-foreground hover:text-primary transition-colors">Measuring For Delivery</NavLink></li>
                  <li><NavLink to="/premium-furniture-care" onClick={handleNavClick} className="text-muted-foreground hover:text-primary transition-colors">Premium Furniture Care Solutions</NavLink></li>
                  <li><NavLink to="/optional-protection-plan" onClick={handleNavClick} className="text-muted-foreground hover:text-primary transition-colors">Optional Protection Plan</NavLink></li>
                </ul>
              </div>
            </>
          ) : (
            <div>
              <h3 className="font-semibold text-foreground mb-4">Navigate</h3>
              <ul className="space-y-2">
                <li><NavLink to="/login" onClick={handleNavClick} className="text-muted-foreground hover:text-primary transition-colors">Login</NavLink></li>
                <li><NavLink to="/request-access" onClick={handleNavClick} className="text-muted-foreground hover:text-primary transition-colors">Pro Access</NavLink></li>
              </ul>
            </div>
          )}

          {/* Contact */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Contact Us</h3>
            <address className="not-italic space-y-2 text-muted-foreground">
              <p>
                <a href="mailto:sourcing@designvault.ca" className="hover:text-primary transition-colors">sourcing@designvault.ca</a>
              </p>
              <p>
                <a href="tel:2508019237" className="hover:text-primary transition-colors">250-801-9237</a>
              </p>
            </address>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;